if (!customElements.get('layered-pop-carousel')) {
  class LayeredPopCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.viewport = this.querySelector('[data-layered-pop-viewport]');
      this.slidesContainer = this.querySelector('[data-layered-pop-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-layered-pop-slide]'));
      this.previousButton = this.querySelector('[data-layered-pop-previous]');
      this.nextButton = this.querySelector('[data-layered-pop-next]');
      this.status = this.querySelector('[data-layered-pop-status]');
      this.paginationDots = Array.from(this.querySelectorAll('[data-layered-pop-dot]'));
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active')),
      );
      this.transitionToken = 0;
      this.isAnimating = false;

      this.handlePreviousBound = () => this.requestSlide(this.currentIndex - 1);
      this.handleNextBound = () => this.requestSlide(this.currentIndex + 1);
      this.handleKeydownBound = this.handleKeydown.bind(this);
      this.handlePointerDownBound = this.handlePointerDown.bind(this);
      this.handlePointerUpBound = this.handlePointerUp.bind(this);
      this.handlePointerCancelBound = this.handlePointerCancel.bind(this);
      this.handleBlockSelectBound = this.handleBlockSelect.bind(this);
      this.handleReducedMotionChangeBound = this.handleReducedMotionChange.bind(this);
      this.handleViewportChangeBound = this.handleViewportChange.bind(this);

      this.previousButton?.addEventListener('click', this.handlePreviousBound);
      this.nextButton?.addEventListener('click', this.handleNextBound);
      this.viewport?.addEventListener('keydown', this.handleKeydownBound);
      this.viewport?.addEventListener('pointerdown', this.handlePointerDownBound);
      this.viewport?.addEventListener('pointerup', this.handlePointerUpBound);
      this.viewport?.addEventListener('pointercancel', this.handlePointerCancelBound);
      this.addEventListener('shopify:block:select', this.handleBlockSelectBound);
      this.reducedMotionMedia.addEventListener('change', this.handleReducedMotionChangeBound);
      this.mobileMedia.addEventListener('change', this.handleViewportChangeBound);

      this.syncControls();
      this.syncPagination();

      if (!this.slides.length) {
        this.dataset.initialState = 'complete';
        return;
      }

      this.syncStageAspectRatio(this.slides[this.currentIndex]);

      if (this.reducedMotionMedia.matches) {
        this.revealSlideLayers(this.slides[this.currentIndex]);
        this.dataset.initialState = 'complete';
      } else {
        this.observeInitialEntry();
      }

      this.scheduleAdjacentPreload();
    }

    disconnectedCallback() {
      this.transitionToken += 1;
      this.initialObserver?.disconnect();
      this.clearIdleCallback();
      this.cancelAnimations();

      this.previousButton?.removeEventListener('click', this.handlePreviousBound);
      this.nextButton?.removeEventListener('click', this.handleNextBound);
      this.viewport?.removeEventListener('keydown', this.handleKeydownBound);
      this.viewport?.removeEventListener('pointerdown', this.handlePointerDownBound);
      this.viewport?.removeEventListener('pointerup', this.handlePointerUpBound);
      this.viewport?.removeEventListener('pointercancel', this.handlePointerCancelBound);
      this.removeEventListener('shopify:block:select', this.handleBlockSelectBound);
      this.reducedMotionMedia?.removeEventListener('change', this.handleReducedMotionChangeBound);
      this.mobileMedia?.removeEventListener('change', this.handleViewportChangeBound);

      this.isInitialized = false;
      this.isAnimating = false;
      this.pointerStart = null;
    }

    observeInitialEntry() {
      if (typeof IntersectionObserver === 'undefined') {
        this.playInitialEntry();
        return;
      }

      this.initialObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.25)) return;

          this.initialObserver.disconnect();
          this.playInitialEntry();
        },
        { threshold: [0.25] },
      );
      this.initialObserver.observe(this);
    }

    async playInitialEntry() {
      if (this.dataset.initialState !== 'pending' || !this.slides.length) return;

      const token = ++this.transitionToken;
      const activeSlide = this.slides[this.currentIndex];
      this.isAnimating = true;
      this.classList.add('is-animating');
      this.viewport?.setAttribute('aria-busy', 'true');
      this.hideSlideLayers(activeSlide);
      this.syncControls();

      await this.waitForSlideImages(activeSlide);
      if (!this.isTransitionCurrent(token)) return;

      this.dataset.initialState = 'animating';
      await this.animateLayers(activeSlide, 'enter');
      if (!this.isTransitionCurrent(token)) return;

      this.dataset.initialState = 'complete';
      this.finishInteraction();
      this.scheduleAdjacentPreload();
    }

    async requestSlide(targetIndex) {
      if (
        this.isAnimating ||
        targetIndex < 0 ||
        targetIndex >= this.slides.length ||
        targetIndex === this.currentIndex
      ) {
        return;
      }

      this.completePendingInitialState();
      const token = ++this.transitionToken;
      const currentSlide = this.slides[this.currentIndex];
      const targetSlide = this.slides[targetIndex];
      this.isAnimating = true;
      this.classList.add('is-animating');
      this.viewport?.setAttribute('aria-busy', 'true');
      this.syncControls();

      await this.waitForSlideImages(targetSlide);
      if (!this.isTransitionCurrent(token)) return;

      // 切换严格串行：当前图层收回、整页横向轮播、下一页图层弹出。
      if (!this.reducedMotionMedia.matches) {
        await this.animateLayers(currentSlide, 'exit');
        if (!this.isTransitionCurrent(token)) return;

        await this.slideBetween(currentSlide, targetSlide, targetIndex > this.currentIndex ? 1 : -1);
        if (!this.isTransitionCurrent(token)) return;
      }

      this.activateSlide(targetIndex);

      if (!this.reducedMotionMedia.matches) {
        await this.animateLayers(targetSlide, 'enter');
        if (!this.isTransitionCurrent(token)) return;
      } else {
        this.revealSlideLayers(targetSlide);
      }

      this.finishInteraction();
      this.scheduleAdjacentPreload();
    }

    completePendingInitialState() {
      if (this.dataset.initialState !== 'pending') return;

      this.initialObserver?.disconnect();
      this.revealSlideLayers(this.slides[this.currentIndex]);
      this.dataset.initialState = 'complete';
    }

    activateSlide(targetIndex) {
      this.slides.forEach((slide, index) => {
        const isTarget = index === targetIndex;
        slide.classList.toggle('is-active', isTarget);
        slide.classList.toggle('is-visible', isTarget);
        slide.classList.remove('is-target');
        slide.setAttribute('aria-hidden', String(!isTarget));
        slide.style.transform = '';

        const base = slide.querySelector('[data-layered-pop-base]');
        if (base) base.style.opacity = '';
      });

      this.currentIndex = targetIndex;
      this.syncStageAspectRatio(this.slides[targetIndex]);
      this.syncPagination();
      this.updateStatus();
    }

    async slideBetween(currentSlide, targetSlide, direction) {
      this.hideSlideLayers(targetSlide);
      targetSlide.classList.add('is-visible', 'is-target');
      targetSlide.setAttribute('aria-hidden', 'true');

      const currentEnd = direction > 0 ? '-100%' : '100%';
      const targetStart = direction > 0 ? '100%' : '-100%';
      currentSlide.style.transform = 'translate3d(0, 0, 0)';
      targetSlide.style.transform = `translate3d(${targetStart}, 0, 0)`;

      const animationOptions = {
        duration: 520,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      };
      const currentHeight = this.slidesContainer.getBoundingClientRect().height;
      const targetRatio = this.getSlideAspectRatio(targetSlide);
      const targetHeight = this.slidesContainer.clientWidth / targetRatio;
      this.slidesContainer.style.height = `${currentHeight}px`;

      const currentAnimation = currentSlide.animate(
        [
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(${currentEnd}, 0, 0)` },
        ],
        animationOptions,
      );
      const targetAnimation = targetSlide.animate(
        [
          { transform: `translate3d(${targetStart}, 0, 0)` },
          { transform: 'translate3d(0, 0, 0)' },
        ],
        animationOptions,
      );
      const stageAnimation = this.slidesContainer.animate(
        [{ height: `${currentHeight}px` }, { height: `${targetHeight}px` }],
        animationOptions,
      );

      await Promise.all([
        currentAnimation.finished.catch(() => undefined),
        targetAnimation.finished.catch(() => undefined),
        stageAnimation.finished.catch(() => undefined),
      ]);

      currentSlide.style.transform = `translate3d(${currentEnd}, 0, 0)`;
      targetSlide.style.transform = 'translate3d(0, 0, 0)';
      this.slidesContainer.style.aspectRatio = String(targetRatio);
      this.slidesContainer.style.height = `${targetHeight}px`;
      currentAnimation.cancel();
      targetAnimation.cancel();
      stageAnimation.cancel();
      this.slidesContainer.style.height = '';
    }

    async animateLayers(slide, direction) {
      const layers = Array.from(slide.querySelectorAll('[data-layered-pop-layer]')).sort(
        (firstLayer, secondLayer) =>
          Number.parseInt(firstLayer.dataset.layerOrder, 10) -
          Number.parseInt(secondLayer.dataset.layerOrder, 10),
      );

      if (!layers.length || this.reducedMotionMedia.matches) {
        if (direction === 'enter') this.revealSlideLayers(slide);
        else this.hideSlideLayers(slide);
        return;
      }

      if (direction === 'exit') layers.reverse();

      const duration = direction === 'enter' ? 620 : 420;
      const stagger = direction === 'enter' ? 90 : 70;
      const easing = direction === 'enter' ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'cubic-bezier(0.7, 0, 0.84, 0)';
      const animations = layers.map((layer, index) => {
        const motionElement = layer.querySelector('[data-layered-pop-layer-motion]');
        const collapsedTransform = this.getCollapsedTransform(slide, layer);
        const expandedFrame = {
          opacity: 1,
          transform: 'translate3d(0, 0, 0) scale(1) rotateX(0deg)',
        };
        const collapsedFrame = {
          opacity: 0,
          transform: collapsedTransform,
        };
        const keyframes = direction === 'enter' ? [collapsedFrame, expandedFrame] : [expandedFrame, collapsedFrame];

        motionElement.style.opacity = direction === 'enter' ? '0' : '1';
        motionElement.style.transform = direction === 'enter' ? collapsedTransform : 'none';

        const animation = motionElement.animate(keyframes, {
          duration,
          delay: index * stagger,
          easing,
          fill: 'both',
        });

        return { animation, motionElement, collapsedTransform };
      });

      await Promise.all(animations.map(({ animation }) => animation.finished.catch(() => undefined)));

      animations.forEach(({ animation, motionElement, collapsedTransform }) => {
        motionElement.style.opacity = direction === 'enter' ? '1' : '0';
        motionElement.style.transform = direction === 'enter' ? 'none' : collapsedTransform;
        animation.cancel();
      });
    }

    getCollapsedTransform(slide, layer) {
      // 根据当前响应式画布实时计算图层中心到弹出原点的位移，避免缩放后坐标失真。
      const stageRect = this.slidesContainer.getBoundingClientRect();
      const layerRect = layer.getBoundingClientRect();
      const isMobile = this.mobileMedia.matches;
      const originX = this.getNumber(
        isMobile ? slide.dataset.originXMobile : slide.dataset.originXDesktop,
        50,
      );
      const originY = this.getNumber(
        isMobile ? slide.dataset.originYMobile : slide.dataset.originYDesktop,
        52,
      );
      const originLeft = stageRect.left + stageRect.width * (originX / 100);
      const originTop = stageRect.top + stageRect.height * (originY / 100);
      const layerCenterX = layerRect.left + layerRect.width / 2;
      const layerCenterY = layerRect.top + layerRect.height / 2;
      const translateX = originLeft - layerCenterX;
      const translateY = originTop - layerCenterY;

      return `translate3d(${translateX}px, ${translateY}px, 0) scale(0.18) rotateX(68deg)`;
    }

    revealSlideLayers(slide) {
      slide?.querySelectorAll('[data-layered-pop-layer-motion]').forEach((motionElement) => {
        motionElement.getAnimations().forEach((animation) => animation.cancel());
        motionElement.style.opacity = '1';
        motionElement.style.transform = 'none';
      });
    }

    hideSlideLayers(slide) {
      slide?.querySelectorAll('[data-layered-pop-layer-motion]').forEach((motionElement) => {
        motionElement.getAnimations().forEach((animation) => animation.cancel());
        motionElement.style.opacity = '0';
        motionElement.style.transform = 'none';
      });
    }

    async waitForSlideImages(slide) {
      const images = Array.from(slide.querySelectorAll('img'));
      if (!images.length) return;

      const decodeTasks = images.map((image) => {
        image.loading = 'eager';
        if (typeof image.decode === 'function') return image.decode().catch(() => undefined);
        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      });

      await Promise.race([
        Promise.all(decodeTasks),
        new Promise((resolve) => window.setTimeout(resolve, 1200)),
      ]);
    }

    scheduleAdjacentPreload() {
      this.clearIdleCallback();

      const preload = () => {
        [this.currentIndex - 1, this.currentIndex + 1].forEach((index) => {
          const slide = this.slides[index];
          if (!slide) return;

          slide.querySelectorAll('img').forEach((image) => {
            image.loading = 'eager';
            image.decode?.().catch(() => undefined);
          });
        });
      };

      if ('requestIdleCallback' in window) {
        this.idleCallback = window.requestIdleCallback(preload, { timeout: 1000 });
      } else {
        this.idleCallback = window.setTimeout(preload, 200);
      }
    }

    clearIdleCallback() {
      if (this.idleCallback == null) return;

      if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.idleCallback);
      else window.clearTimeout(this.idleCallback);
      this.idleCallback = null;
    }

    handleKeydown(event) {
      if (event.target.closest('button, a, input, select, textarea')) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.requestSlide(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.requestSlide(this.currentIndex + 1);
      }
    }

    handlePointerDown(event) {
      if (
        event.pointerType === 'mouse' ||
        event.button !== 0 ||
        this.isAnimating ||
        event.target.closest('button, a, input, select, textarea')
      ) {
        return;
      }

      this.pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
    }

    handlePointerUp(event) {
      if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      const threshold = Math.max(48, (this.viewport?.clientWidth || 0) * 0.06);
      this.pointerStart = null;

      // 只有横向意图足够明确时才切页，保证移动端纵向滚动不受影响。
      if (Math.abs(deltaX) < threshold || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

      this.requestSlide(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
    }

    handlePointerCancel() {
      this.pointerStart = null;
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-layered-pop-slide]');
      const targetIndex = Number.parseInt(selectedSlide?.dataset.slideIndex, 10);
      if (Number.isNaN(targetIndex)) return;

      this.showSlideImmediately(targetIndex);
    }

    handleReducedMotionChange(event) {
      if (!event.matches) return;

      this.showSlideImmediately(this.currentIndex);
      this.dataset.initialState = 'complete';
    }

    handleViewportChange() {
      if (this.isAnimating) {
        this.showSlideImmediately(this.currentIndex);
        return;
      }

      this.syncStageAspectRatio(this.slides[this.currentIndex]);
    }

    showSlideImmediately(targetIndex) {
      if (targetIndex < 0 || targetIndex >= this.slides.length) return;

      this.transitionToken += 1;
      this.initialObserver?.disconnect();
      this.cancelAnimations();
      this.activateSlide(targetIndex);
      this.revealSlideLayers(this.slides[targetIndex]);
      this.dataset.initialState = 'complete';
      this.finishInteraction();
    }

    cancelAnimations() {
      this.querySelectorAll('[data-layered-pop-slide], [data-layered-pop-layer-motion], [data-layered-pop-base]').forEach(
        (element) => {
          element.getAnimations().forEach((animation) => animation.cancel());
        },
      );
      this.slidesContainer?.getAnimations().forEach((animation) => animation.cancel());
      if (this.slidesContainer) this.slidesContainer.style.height = '';
    }

    syncStageAspectRatio(slide) {
      if (!this.slidesContainer || !slide) return;

      this.slidesContainer.style.aspectRatio = String(this.getSlideAspectRatio(slide));
    }

    getSlideAspectRatio(slide) {
      const ratio = this.getNumber(
        this.mobileMedia.matches ? slide?.dataset.aspectRatioMobile : slide?.dataset.aspectRatioDesktop,
        1.5,
      );
      return ratio > 0 ? ratio : 1.5;
    }

    finishInteraction() {
      this.isAnimating = false;
      this.classList.remove('is-animating');
      this.viewport?.setAttribute('aria-busy', 'false');
      this.syncControls();
    }

    syncControls() {
      const controlsLocked = this.isAnimating || this.slides.length <= 1;
      this.setButtonState(this.previousButton, controlsLocked || this.currentIndex === 0);
      this.setButtonState(this.nextButton, controlsLocked || this.currentIndex === this.slides.length - 1);
    }

    syncPagination() {
      this.paginationDots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.currentIndex);
      });
    }

    setButtonState(button, disabled) {
      if (!button) return;

      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }

    updateStatus() {
      if (!this.status || !this.slides.length) return;

      this.status.textContent = `Slide ${this.currentIndex + 1} of ${this.slides.length}`;
    }

    isTransitionCurrent(token) {
      return this.isConnected && token === this.transitionToken;
    }

    getNumber(value, fallback) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    }
  }

  customElements.define('layered-pop-carousel', LayeredPopCarousel);
}
